/**
 * Parse search query into structured filters.
 * Supports: tag:name, notebook:name, is:pinned, is:favorited, is:archived, date:YYYY-MM, free text
 */
export function parseSearchQuery(query, tags, notebooks) {
  if (!query || !query.trim()) return { text: '', filters: {} };

  const filters = {};
  let remaining = query;

  // tag:name
  const tagMatches = [...remaining.matchAll(/tag:(\S+)/gi)];
  if (tagMatches.length > 0) {
    filters.tagNames = tagMatches.map((m) => m[1].toLowerCase());
    // Resolve to tag IDs
    filters.tagIds = filters.tagNames
      .map((name) => tags.find((t) => t.name.toLowerCase() === name))
      .filter(Boolean)
      .map((t) => t.id);
    remaining = remaining.replace(/tag:\S+/gi, '');
  }

  // notebook:name
  const nbMatches = [...remaining.matchAll(/notebook:(\S+)/gi)];
  if (nbMatches.length > 0) {
    filters.notebookNames = nbMatches.map((m) => m[1].toLowerCase());
    filters.notebookIds = filters.notebookNames
      .map((name) => notebooks.find((nb) => nb.name.toLowerCase() === name))
      .filter(Boolean)
      .map((nb) => nb.id);
    remaining = remaining.replace(/notebook:\S+/gi, '');
  }

  // is:pinned, is:favorited, is:archived, is:encrypted
  const isMatches = [...remaining.matchAll(/is:(\S+)/gi)];
  if (isMatches.length > 0) {
    filters.flags = isMatches.map((m) => m[1].toLowerCase());
    remaining = remaining.replace(/is:\S+/gi, '');
  }

  // date:YYYY-MM or date:YYYY-MM-DD
  const dateMatch = remaining.match(/date:(\S+)/i);
  if (dateMatch) {
    filters.datePrefix = dateMatch[1];
    remaining = remaining.replace(/date:\S+/gi, '');
  }

  return {
    text: remaining.trim(),
    filters,
  };
}

/**
 * Apply parsed filters to a list of notes.
 */
export function applySearchFilters(notes, parsed) {
  let result = [...notes];
  const { text, filters } = parsed;

  // Text search
  if (text) {
    const lower = text.toLowerCase();
    result = result.filter(
      (n) =>
        (n.title || '').toLowerCase().includes(lower) ||
        (n.content || '').toLowerCase().includes(lower)
    );
  }

  // Tag filter
  if (filters.tagIds?.length > 0) {
    result = result.filter((n) =>
      filters.tagIds.some((tid) => (n.tags || []).includes(tid))
    );
  }

  // Notebook filter
  if (filters.notebookIds?.length > 0) {
    result = result.filter((n) => filters.notebookIds.includes(n.notebook_id));
  }

  // Flags
  if (filters.flags) {
    for (const flag of filters.flags) {
      switch (flag) {
        case 'pinned': result = result.filter((n) => n.is_pinned); break;
        case 'favorited':
        case 'favorite': result = result.filter((n) => n.is_favorited); break;
        case 'archived': result = result.filter((n) => n.is_archived); break;
        case 'encrypted': result = result.filter((n) => n.is_encrypted); break;
        case 'shared': result = result.filter((n) => n.is_shared); break;
        default: break;
      }
    }
  }

  // Date prefix filter
  if (filters.datePrefix) {
    result = result.filter((n) =>
      (n.created_at || '').startsWith(filters.datePrefix)
    );
  }

  return result;
}
